import type { SavedMapPlotRequestDetail } from "@/features/map/types";
import type { AcquireMultipleBookHandoffData } from "@/types/acquirebook";
import type { IPrefecture } from "@/types/prefectures";
import { getBookTypeFromRealEstateTypeLabel } from "@/features/map/components/AcquireMultipleBooksModal";
import { isValidSavedPlotForAcquireBook } from "@/utils/savedPlotValidation";

/**
 * SavedMapPlotRequestDetailをAcquireMultipleBookHandoffDataに変換
 * @param details 変換元のプロット詳細配列
 * @param prefectures 都道府県マスタ
 * @returns 変換後のデータ（無効な行は除外される）
 */
export const convertToAcquireMultipleBookData = (
  details: SavedMapPlotRequestDetail[],
  prefectures: IPrefecture[]
): AcquireMultipleBookHandoffData[] => {
  // エラー行を除外し、所在・地番があるもののみをフィルタリング
  const validDetails = details.filter((detail) =>
    isValidSavedPlotForAcquireBook(detail, { checkStatus: true })
  );

  // 都道府県名→都道府県データのMapを事前作成（パフォーマンス向上のため）
  const prefMap = new Map(prefectures.map((p) => [p.name, p]));

  return validDetails
    .map((detail, index): AcquireMultipleBookHandoffData | null => {
      // 都道府県コードを取得
      const pref = prefMap.get(detail.prefName ?? "");
      if (!pref) {
        // 都道府県が見つからない場合は処理対象外とする（意図しない登記申請を防ぐため）
        return null;
      }
      const prefCode = pref.prefCode;

      const realEstateType = detail.realEstateType ?? "土地";
      const bookType = getBookTypeFromRealEstateTypeLabel(realEstateType);

      // 地番または家屋番号
      const chibanOrKaokuNumber =
        realEstateType === "土地"
          ? detail.requestChiban ?? ""
          : detail.requestKaokuNumber ?? "";

      return {
        id: index + 1,
        bookType,
        prefCode,
        prefName: detail.prefName ?? "",
        locationFull: detail.requestLocation ?? "",
        chibanKaokuNumber: chibanOrKaokuNumber,
        kyoutan: false,
        sintaku: false,
        genzai: false,
        ownerInfo: false,
        electricDrawings: false,
        tisekiDrawings: false,
        tiekiDrawings: false,
        buildingDrawings: false,
      };
    })
    .filter((item): item is AcquireMultipleBookHandoffData => item !== null);
};
